// Content data managed via TanStack Query
// Provides query options and hooks for fetching content requirements from legacy Aspirex system

import {
  useMutation,
  useQueryClient,
  queryOptions,
  useQuery,
} from "@tanstack/react-query";
import { trpcClient } from "../trpc";

// ============================================================================
// Query Keys
// ============================================================================

export const contentKeys = {
  all: ["content"] as const,
  contentRequirements: (secureUrl: string, programId?: number) =>
    [...contentKeys.all, "requirements", secureUrl, programId] as const,
  instagramStories: (
    accountId: string,
    externalPostProjectId: number,
    forceUpdate?: boolean,
  ) =>
    [
      ...contentKeys.all,
      "instagram-stories",
      accountId,
      externalPostProjectId,
      forceUpdate,
    ] as const,
};

// ============================================================================
// Types - Inferred from API response
// ============================================================================

type ContentRequirementsResult = Awaited<
  ReturnType<typeof trpcClient.content.getContentRequirements.query>
>;

export type ContentRequirement =
  ContentRequirementsResult["contentRequirements"][number];
export type ContentRequirementDeliverable = ContentRequirement["deliverable"];
export type ContentRequirementSocialAccount =
  ContentRequirement["socialAccounts"][number];

// Content state enum matching the legacy system (all GraphQL ContentState values)
export const ContentState = {
  ACCEPTED: "ACCEPTED",
  AMENDED: "AMENDED",
  COMPLETED: "COMPLETED",
  COMPLETED_WITHOUT_PAYMENT: "COMPLETED_WITHOUT_PAYMENT",
  GCR_PENDING: "GCR_PENDING",
  MARKED_COMPLETE: "MARKED_COMPLETE",
  NEW: "NEW",
  PENDING_MARK_COMPLETE: "PENDING_MARK_COMPLETE",
  PLACEHOLDER: "PLACEHOLDER",
  REJECTED: "REJECTED",
  SOFT_APPROVED: "SOFT_APPROVED",
  SOFT_REJECTED: "SOFT_REJECTED",
  UPLOADED: "UPLOADED",
  VALIDATING_UPLOAD: "VALIDATING_UPLOAD",
} as const;

export type ContentStateType = (typeof ContentState)[keyof typeof ContentState];

// States that represent a completed/terminal content requirement.
// Includes SOFT_APPROVED and PENDING_MARK_COMPLETE to match the legacy
// system's counting, which considers these as fulfilled deliverables.
// Note: ACCEPTED (approved) is NOT included because the creator still needs
// to submit the live post link after approval.
const COMPLETED_STATES = new Set<string>([
  ContentState.COMPLETED,
  ContentState.MARKED_COMPLETE,
  ContentState.COMPLETED_WITHOUT_PAYMENT,
  ContentState.SOFT_APPROVED,
  ContentState.PENDING_MARK_COMPLETE,
]);

// ============================================================================
// Query Options
// ============================================================================

/**
 * Query options for fetching content requirements by secureUrl.
 */
export function contentRequirementsQueryOptions(
  organizationId: string,
  secureUrl: string,
  profileId: string,
  programId?: number,
) {
  return queryOptions({
    queryKey: contentKeys.contentRequirements(secureUrl, programId),
    queryFn: () =>
      trpcClient.content.getContentRequirements.query({
        organizationId,
        secureUrl,
        profileId,
        programId,
      }),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    enabled: !!secureUrl && !!profileId,
  });
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook for fetching content requirements.
 */
export function useContentRequirementsQuery(
  organizationId: string,
  secureUrl: string,
  profileId: string,
  programId?: number,
) {
  return useQuery(
    contentRequirementsQueryOptions(
      organizationId,
      secureUrl,
      profileId,
      programId,
    ),
  );
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if a content requirement is completed.
 * A requirement is completed when it has a non-zero completedContentCount
 * or is in a completed/approved state. This includes SOFT_APPROVED and
 * PENDING_MARK_COMPLETE to align with the legacy system's interaction
 * status counting.
 *
 * Note: ACCEPTED (approved) is not a completed state because the creator
 * still needs to submit the live post link after content approval.
 */
export function isContentRequirementCompleted(
  contentRequirement: ContentRequirement,
): boolean {
  const { state, completedContentCount } = contentRequirement;
  return completedContentCount > 0 || COMPLETED_STATES.has(state);
}

/**
 * Get the due date timestamp for a content requirement (for sorting).
 * Uses submission date when approval is required, otherwise completion date
 * — same logic the deliverable cards use to display due dates.
 */
function getDueDateTimestamp(req: ContentRequirement): number {
  const dueDates = req.deliverable?.dueDates;
  const relevantDueDate = req.deliverable?.requiresApproval
    ? dueDates?.submission
    : dueDates?.completion;
  const dateStr = relevantDueDate?.endDate || relevantDueDate?.startDate;
  return dateStr ? new Date(dateStr).getTime() : Number.MAX_SAFE_INTEGER;
}

/**
 * Filter content requirements into outstanding and completed,
 * sorted in ascending order by due date (earliest first).
 */
export function filterContentRequirements(
  contentRequirements: ContentRequirement[],
) {
  const outstanding: ContentRequirement[] = [];
  const completed: ContentRequirement[] = [];

  for (const req of contentRequirements) {
    // EPD-9024: Custom Work ("OTHER") is now a real submittable deliverable, so it
    // is no longer skipped here — it routes to outstanding/completed like any other.
    if (isContentRequirementCompleted(req)) {
      completed.push(req);
    } else {
      outstanding.push(req);
    }
  }

  outstanding.sort((a, b) => getDueDateTimestamp(a) - getDueDateTimestamp(b));
  completed.sort((a, b) => getDueDateTimestamp(a) - getDueDateTimestamp(b));

  return { outstanding, completed };
}

/**
 * Get a display name for a content requirement.
 *
 * Priority order (matching the member portal):
 * 1. deliverableName — custom label set by the brand per deliverable slot
 * 2. displayName — auto-generated fallback (e.g. "1 Instagram Reel")
 * 3. deliverable.name — template-level name
 * 4. "Content" — last-resort fallback
 */
export function getContentDisplayName(
  contentRequirement: ContentRequirement,
): string {
  return (
    contentRequirement.deliverableName ||
    contentRequirement.displayName ||
    contentRequirement.deliverable?.name ||
    "Content"
  );
}

/**
 * Check if a content requirement has been submitted and is awaiting brand review.
 * These deliverables should show a read-only view instead of the submission dialog.
 */
export function isAwaitingReview(
  contentRequirement: ContentRequirement,
): boolean {
  const { state, deliverable } = contentRequirement;
  if (!deliverable?.requiresApproval) return false;

  return (
    state === ContentState.NEW ||
    state === ContentState.UPLOADED ||
    state === ContentState.VALIDATING_UPLOAD ||
    state === ContentState.AMENDED ||
    state === ContentState.GCR_PENDING
  );
}

/**
 * Get the button title based on content state.
 * Handles all GraphQL ContentState values with explicit cases and a safe default.
 */
export function getButtonTitle(contentRequirement: ContentRequirement): string {
  const { state, deliverable, dateLastReject } = contentRequirement;

  // If rejected and has rejection date, show "Resubmit"
  if (dateLastReject && state === ContentState.REJECTED) {
    return "Resubmit";
  }

  // Handle completed states - show "View"
  if (COMPLETED_STATES.has(state)) {
    return "View";
  }

  // If requires approval
  if (deliverable?.requiresApproval) {
    switch (state) {
      case ContentState.NEW:
      case ContentState.AMENDED:
      case ContentState.UPLOADED:
      case ContentState.VALIDATING_UPLOAD:
        return "View Submission";
      case ContentState.ACCEPTED:
        return "Submit Post Link";
      case ContentState.REJECTED:
      case ContentState.SOFT_REJECTED:
        return "Resubmit";
      case ContentState.GCR_PENDING:
        return "Pending Review";
      case ContentState.PLACEHOLDER:
        return "Submit for Review";
      default:
        return "Submit for Review";
    }
  }

  return "Submit";
}

/**
 * Post type display names.
 */
export const POST_TYPE_DISPLAY_NAMES: Record<string, string> = {
  INSTAGRAM: "Instagram Post",
  INSTAGRAM_POST: "Instagram Post",
  INSTAGRAM_REEL: "Instagram Reel",
  INSTAGRAM_STORY: "Instagram Story",
  INSTAGRAM_VIDEO: "Instagram Video",
  TIKTOK_VIDEO: "TikTok Video",
  YOUTUBE_DEDICATED: "YouTube Video",
  YOUTUBE_MENTION: "YouTube Product Mention",
  YOUTUBE_SHORT: "YouTube Short",
  FACEBOOK_POST: "Facebook Post",
  PINTEREST: "Pinterest Pin",
  PINTEREST_PIN: "Pinterest Pin",
  BLOG_DEDICATED: "Blog Post",
  BLOG_MENTION: "Blog Mention",
  TWITTER_POST: "Twitter Post",
  ADDITIONAL_IMAGES: "Additional Images",
  ADDITIONAL_VIDEOS: "Additional Videos",
  FLEXIBLE: "Deliverable",
  OTHER: "Custom Work",
};

/**
 * Get display name for a post type.
 */
export function getPostTypeDisplayName(postType: string | null): string {
  if (!postType) return "Content";
  // Post types arrive in mixed case ("OTHER" from acceptedPostTypes, "other"
  // from deliverable.postType); the map keys are all uppercase.
  return POST_TYPE_DISPLAY_NAMES[postType.toUpperCase()] || postType;
}

// ============================================================================
// Content Submission Types
// ============================================================================

/**
 * Media input for content submission.
 */
export interface MediaInput {
  url: string;
  mediaType?: string | null;
  contentType?: string | null;
  previewUrl?: string | null;
  size?: number | null;
}

/**
 * Input for submitting content.
 */
export interface SubmitContentInput {
  organizationId: string;
  profileId: string;
  externalPostProjectId: number;
  contentReviewId: number;
  programId: number;
  media?: MediaInput[] | null;
  caption?: string | null;
  link?: string | null;
  storyIds?: string[] | null;
  creatorGuidelineCheckoff?: boolean[] | null;
  uploadedPostType?: string | null;
  handle?: string | null;
  contentFieldValues?: Record<string, unknown> | null;
}

// ============================================================================
// Content Submission Mutations
// ============================================================================

/**
 * Hook for getting a presigned upload URL.
 */
export function useGetUploadUrlMutation() {
  return useMutation({
    mutationFn: async (input: {
      organizationId: string;
      profileId: string;
      fileName: string;
      contentType: string;
    }) => {
      return trpcClient.content.getUploadUrl.mutate({
        organizationId: input.organizationId,
        profileId: input.profileId,
        fileName: input.fileName,
        contentType: input.contentType,
      });
    },
  });
}

/**
 * Hook for submitting content.
 */
export function useSubmitContentMutation(
  secureUrl: string,
  programId?: number,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SubmitContentInput) => {
      return trpcClient.content.submitContent.mutate({
        organizationId: input.organizationId,
        profileId: input.profileId,
        externalPostProjectId: input.externalPostProjectId,
        contentReviewId: input.contentReviewId,
        programId: input.programId,
        media: input.media,
        caption: input.caption,
        link: input.link,
        storyIds: input.storyIds,
        creatorGuidelineCheckoff: input.creatorGuidelineCheckoff,
        uploadedPostType: input.uploadedPostType,
        handle: input.handle,
        contentFieldValues: input.contentFieldValues,
      });
    },
    onSuccess: () => {
      // Invalidate content requirements to refetch updated data
      queryClient.invalidateQueries({
        queryKey: contentKeys.contentRequirements(secureUrl, programId),
      });
    },
  });
}

// ============================================================================
// Content Fields
// ============================================================================

type ContentFieldsResult = Awaited<
  ReturnType<typeof trpcClient.content.getContentFields.query>
>;

export type ContentField = ContentFieldsResult["contentFields"][number];

/**
 * Query options for fetching content field definitions for a program.
 */
export function contentFieldsQueryOptions(
  organizationId: string,
  profileId: string,
  programId: number,
  clientId?: string,
) {
  return queryOptions({
    queryKey: [
      ...contentKeys.all,
      "fields",
      organizationId,
      profileId,
      programId,
      clientId,
    ],
    queryFn: () =>
      trpcClient.content.getContentFields.query({
        organizationId,
        profileId,
        programId,
        clientId,
      }),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for fetching content field definitions.
 */
export function useContentFields(
  organizationId: string,
  profileId: string,
  programId: number,
  clientId?: string,
  enabled = true,
) {
  return useQuery({
    ...contentFieldsQueryOptions(
      organizationId,
      profileId,
      programId,
      clientId,
    ),
    enabled,
  });
}

// ============================================================================
// File Upload Helpers
// ============================================================================

/**
 * Determine if a file is a video based on MIME type.
 */
export function isVideoFile(file: File): boolean {
  return file.type.startsWith("video/");
}

/**
 * Determine if a file is an image based on MIME type.
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith("image/");
}

/**
 * Get the content type category for upload URL request.
 */
export function getContentTypeCategory(file: File): string {
  if (isVideoFile(file)) return "video";
  if (isImageFile(file)) return "image";
  // HEIC/HEIF files from iOS may have an empty MIME type — fall back to extension check
  const ext = file.name.toLowerCase();
  if (ext.endsWith(".heic") || ext.endsWith(".heif")) return "image";
  return "file";
}

/**
 * Accepted file types by post type.
 */
export const ACCEPTED_FILE_TYPES: Record<string, string> = {
  ADDITIONAL_IMAGES: "image/*,.heic,.heif",
  ADDITIONAL_VIDEOS: "video/*",
  BLOG_DEDICATED: ".pdf,.doc,.docx",
  BLOG_MENTION: ".pdf,.doc,.docx",
  FACEBOOK_POST: ".pdf,.doc,.docx,image/*,video/*,.heic,.heif",
  FLEXIBLE: ".pdf,.doc,.docx,image/*,video/*,.heic,.heif",
  INSTAGRAM: "image/*,video/*,.heic,.heif",
  INSTAGRAM_POST: "image/*,.heic,.heif",
  INSTAGRAM_REEL: "video/*",
  INSTAGRAM_STORY: "image/*,video/*,.heic,.heif",
  INSTAGRAM_VIDEO: "video/*",
  OTHER: ".pdf,.doc,.docx,image/*,video/*,.heic,.heif",
  PINTEREST: "image/*,video/*,.heic,.heif",
  PINTEREST_PIN: "image/*,video/*,.heic,.heif",
  SNAPCHAT_STORY: "image/*,video/*,.heic,.heif",
  TIKTOK_VIDEO: "video/*",
  TWITTER_POST: "image/*,video/*,.heic,.heif",
  YOUTUBE_DEDICATED: "video/*",
  YOUTUBE_MENTION: "video/*",
  YOUTUBE_SHORT: "video/*",
};

/**
 * Check if a file matches the accepted file types string (e.g. "image/*,video/*,.heic").
 * Supports MIME wildcards ("image/*"), exact MIME types, and extensions (".pdf").
 */
export function isFileTypeAccepted(file: File, acceptedTypes: string): boolean {
  const patterns = acceptedTypes.split(",").map((p) => p.trim().toLowerCase());
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  return patterns.some((pattern) => {
    if (pattern.startsWith(".")) {
      return fileName.endsWith(pattern);
    }
    if (pattern.endsWith("/*")) {
      const prefix = pattern.slice(0, -1); // "image/*" → "image/"
      return fileType.startsWith(prefix);
    }
    return fileType === pattern;
  });
}

/**
 * Get accepted file types for a post type.
 */
export function getAcceptedFileTypes(postType: string | null): string {
  if (!postType) return ".pdf,.doc,.docx,image/*,video/*,.heic,.heif";
  // Normalize: "other" (deliverable.postType) must hit the "OTHER" key, not
  // fall through to the broad default set.
  return (
    ACCEPTED_FILE_TYPES[postType.toUpperCase()] ||
    ".pdf,.doc,.docx,image/*,video/*,.heic,.heif"
  );
}

/**
 * Check if a post type requires a link (direct post) vs file upload.
 */
export function requiresPostLink(postType: string | null): boolean {
  if (!postType) return false;
  // These post types typically require linking to an existing post
  const linkRequiredTypes = [
    "INSTAGRAM",
    "INSTAGRAM_POST",
    "INSTAGRAM_REEL",
    "INSTAGRAM_VIDEO",
    "TIKTOK_VIDEO",
    "YOUTUBE_DEDICATED",
    "YOUTUBE_MENTION",
    "YOUTUBE_SHORT",
    "FACEBOOK_POST",
    "PINTEREST",
    "PINTEREST_PIN",
    "TWITTER_POST",
  ];
  return linkRequiredTypes.includes(postType);
}

/**
 * Check if a post type requires content upload for review.
 */
export function requiresContentUpload(
  postType: string | null,
  requiresApproval: boolean | null | undefined,
): boolean {
  if (!requiresApproval) return false;
  // When approval is required, most content types need file upload first
  return true;
}

/**
 * Check if content requirement is for Instagram Stories.
 */
export function isInstagramStories(postType: string | null): boolean {
  return postType === "INSTAGRAM_STORY";
}

// ============================================================================
// Instagram Stories
// ============================================================================

/**
 * Instagram Story type - transformed for UI consumption.
 */
export interface InstagramStory {
  id: number | null | undefined;
  mediaId: string | null | undefined;
  image: string | null | undefined;
  thumbnailUrl: string | null | undefined;
  video: string | null | undefined;
  caption: string | null | undefined;
  text: string | null | undefined;
  postedTs: number | null | undefined;
  postType: string | null | undefined;
  mediaType: string | null | undefined;
  // Derived fields for UI
  date: string;
  description: string;
  key: string;
  imageUrl: string;
}

/**
 * Query options for fetching Instagram stories.
 */
export function instagramStoriesQueryOptions(
  organizationId: string,
  profileId: string,
  accountId: string,
  externalPostProjectId: number,
  token: string,
  forceUpdate?: boolean,
) {
  return queryOptions({
    queryKey: contentKeys.instagramStories(
      accountId,
      externalPostProjectId,
      forceUpdate,
    ),
    queryFn: () =>
      trpcClient.content.getInstagramStories.query({
        organizationId,
        profileId,
        accountId,
        externalPostProjectId,
        token,
        forceUpdate,
      }),
    staleTime: 60_000, // Stories are relatively stable, 1 minute cache
    gcTime: 5 * 60_000,
    enabled: !!accountId && !!externalPostProjectId && !!token,
  });
}

/**
 * Hook for fetching Instagram stories.
 */
export function useInstagramStoriesQuery(
  organizationId: string,
  profileId: string,
  accountId: string,
  externalPostProjectId: number,
  token: string,
  forceUpdate?: boolean,
) {
  return useQuery(
    instagramStoriesQueryOptions(
      organizationId,
      profileId,
      accountId,
      externalPostProjectId,
      token,
      forceUpdate,
    ),
  );
}

/**
 * Hook for refetching Instagram stories with force update.
 * Returns a function that triggers a refetch with forceUpdate=true.
 */
export function useRefetchInstagramStories(
  organizationId: string,
  profileId: string,
  accountId: string,
  externalPostProjectId: number,
  token: string,
) {
  const queryClient = useQueryClient();

  return async () => {
    // Invalidate all instagram stories queries for this account/project (regardless of forceUpdate value)
    await queryClient.invalidateQueries({
      queryKey: contentKeys.instagramStories(accountId, externalPostProjectId),
      exact: false,
    });

    // Fetch fresh data with forceUpdate=true
    const freshData = await trpcClient.content.getInstagramStories.query({
      organizationId,
      profileId,
      accountId,
      externalPostProjectId,
      token,
      forceUpdate: true,
    });

    // Update the cache with the fresh data for all matching queries
    queryClient.setQueriesData(
      {
        queryKey: contentKeys.instagramStories(
          accountId,
          externalPostProjectId,
        ),
        exact: false,
      },
      freshData,
    );

    return freshData;
  };
}

// ============================================================================
// Extra Content
// ============================================================================

/**
 * Extra content type - uploaded files that aren't part of required deliverables.
 */
export type ExtraContent = Awaited<
  ReturnType<typeof trpcClient.content.getExtraContent.query>
>["data"][number];

/**
 * Extra post type - live links that aren't part of required deliverables.
 */
export type ExtraPost = Awaited<
  ReturnType<typeof trpcClient.content.getExtraPosts.query>
>[number];

// Add extra content keys to the contentKeys object
export const extraContentKeys = {
  extraContent: (externalPostProjectId: number) =>
    [...contentKeys.all, "extra-content", externalPostProjectId] as const,
  extraPosts: (clientId: string, externalPostProjectId: number) =>
    [
      ...contentKeys.all,
      "extra-posts",
      clientId,
      externalPostProjectId,
    ] as const,
};

/**
 * Query options for fetching extra content (uploaded files).
 */
export function extraContentQueryOptions(
  organizationId: string,
  profileId: string,
  externalPostProjectId: number,
  token: string,
) {
  return queryOptions({
    queryKey: extraContentKeys.extraContent(externalPostProjectId),
    queryFn: () =>
      trpcClient.content.getExtraContent.query({
        organizationId,
        profileId,
        externalPostProjectId,
        token,
      }),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    enabled: !!externalPostProjectId && !!token,
  });
}

/**
 * Hook for fetching extra content (uploaded files).
 */
export function useExtraContentQuery(
  organizationId: string,
  profileId: string,
  externalPostProjectId: number,
  token: string,
) {
  return useQuery(
    extraContentQueryOptions(
      organizationId,
      profileId,
      externalPostProjectId,
      token,
    ),
  );
}

/**
 * Query options for fetching extra posts (live links).
 */
export function extraPostsQueryOptions(
  organizationId: string,
  profileId: string,
  clientId: string,
  externalPostProjectId: number,
  token: string,
) {
  return queryOptions({
    queryKey: extraContentKeys.extraPosts(clientId, externalPostProjectId),
    queryFn: () =>
      trpcClient.content.getExtraPosts.query({
        organizationId,
        profileId,
        clientId,
        externalPostProjectId,
        token,
      }),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    enabled: !!clientId && !!externalPostProjectId && !!token,
  });
}

/**
 * Hook for fetching extra posts (live links).
 */
export function useExtraPostsQuery(
  organizationId: string,
  profileId: string,
  clientId: string,
  externalPostProjectId: number,
  token: string,
) {
  return useQuery(
    extraPostsQueryOptions(
      organizationId,
      profileId,
      clientId,
      externalPostProjectId,
      token,
    ),
  );
}

/**
 * Hook for getting a presigned URL for extra content upload.
 */
export function useGetExtraContentUploadUrlMutation() {
  return useMutation({
    mutationFn: async (input: {
      organizationId: string;
      profileId: string;
      fileName: string;
      contentType: string;
      externalPostProjectId: number;
      token: string;
    }) => {
      return trpcClient.content.getExtraContentUploadUrl.mutate({
        organizationId: input.organizationId,
        profileId: input.profileId,
        fileName: input.fileName,
        contentType: input.contentType,
        externalPostProjectId: input.externalPostProjectId,
        token: input.token,
      });
    },
  });
}

/**
 * Hook for submitting extra content (uploaded file).
 */
export function useSubmitExtraContentMutation(externalPostProjectId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      organizationId: string;
      profileId: string;
      externalPostProjectId: number;
      token: string;
      size: number;
      type: string;
      url: string;
    }) => {
      return trpcClient.content.submitExtraContent.mutate({
        organizationId: input.organizationId,
        profileId: input.profileId,
        externalPostProjectId: input.externalPostProjectId,
        token: input.token,
        size: input.size,
        type: input.type,
        url: input.url,
      });
    },
    onSuccess: () => {
      // Invalidate extra content to refetch updated data
      queryClient.invalidateQueries({
        queryKey: extraContentKeys.extraContent(externalPostProjectId),
      });
    },
  });
}

/**
 * Hook for submitting extra post (live link or Instagram stories).
 */
export function useSubmitExtraPostMutation(
  clientId: string,
  externalPostProjectId: number,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      organizationId: string;
      profileId: string;
      externalPostProjectId: number;
      token: string;
      link?: string | null;
      storyIds?: string[] | null;
    }) => {
      return trpcClient.content.submitExtraPost.mutate({
        organizationId: input.organizationId,
        profileId: input.profileId,
        externalPostProjectId: input.externalPostProjectId,
        token: input.token,
        link: input.link,
        storyIds: input.storyIds,
      });
    },
    onSuccess: () => {
      // Invalidate extra posts to refetch updated data
      queryClient.invalidateQueries({
        queryKey: extraContentKeys.extraPosts(clientId, externalPostProjectId),
      });
    },
  });
}
